import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Order, OrderItem } from '@lr/shared';
import { apiFetch } from '../lib/api';
import { useAuth } from '../context/AuthContext';

/** Order shape returned by /api/account/orders — order_items embed the
 *  parent item's image so we can render proper photography on each card. */
type OrderWithItems = Order & {
  order_items?: Array<
    OrderItem & { item?: { main_image_url: string | null; slug: string } | null }
  >;
};

/**
 * "My Rotation" — single-page dashboard. Active rentals up top with images,
 * return-by countdowns, per-order "Request return label" buttons, plus a
 * bulk "Request all" CTA. Past orders below.
 */
export function Account() {
  const { profile } = useAuth();
  const [orders, setOrders] = useState<OrderWithItems[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [requesting, setRequesting] = useState<string | null>(null);
  const [viewingOrder, setViewingOrder] = useState<OrderWithItems | null>(null);

  function load() {
    setLoading(true);
    apiFetch<OrderWithItems[]>('/api/account/orders')
      .then(setOrders)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'))
      .finally(() => setLoading(false));
  }
  useEffect(load, []);

  async function requestReturn(orderId: string) {
    setRequesting(orderId);
    try {
      await apiFetch(`/api/account/orders/${orderId}/request-return`, {
        method: 'POST'
      });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not request return');
    } finally {
      setRequesting(null);
    }
  }

  async function requestAllReturns(orderIds: string[]) {
    setRequesting('all');
    try {
      await Promise.all(
        orderIds.map((id) =>
          apiFetch(`/api/account/orders/${id}/request-return`, { method: 'POST' })
        )
      );
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not request returns');
    } finally {
      setRequesting(null);
    }
  }

  // Anything not finished is "active" from the customer's perspective.
  // Bundle purchases (orders with no rental items) are excluded entirely.
  const isClosed = (s: string) => ['returned', 'cancelled', 'late'].includes(s);
  const isBundleOnly = (o: OrderWithItems) =>
    o.bundle_purchased_id != null && o.credits_used === 0;
  const active = useMemo(
    () => orders.filter((o) => !isBundleOnly(o) && !isClosed(o.status)),
    [orders]
  );
  const past = useMemo(
    () => orders.filter((o) => !isBundleOnly(o) && isClosed(o.status)),
    [orders]
  );
  const balance = profile?.credit_balance ?? 0;
  // Orders that haven't already had a return requested — these are the ones
  // we offer the "Request return label" button for.
  const returnable = active.filter((o) => o.status !== 'returning');

  return (
    <section className="screen">
      <div className="scroll mr-scroll">
        <div className="mr-head">
          <div className="l">
            <h1>MY ROTATION</h1>
            <div className="p">
              {profile?.first_name ? `Welcome back, ${profile.first_name}.` : 'Your items, timeline and credits.'}
            </div>
          </div>
          <Link to="/credits" className="mr-credits" style={{ textDecoration: 'none' }}>
            <div className="label">YOUR CREDITS</div>
            <div className="val">
              {balance} {balance === 1 ? 'CREDIT' : 'CREDITS'}
            </div>
            <div className="sub">
              Never expire <Info />
            </div>
          </Link>
        </div>

        <div className="section-head">
          <div className="ttl">ACTIVE ITEMS</div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {returnable.length > 1 && (
              <button
                onClick={() => void requestAllReturns(returnable.map((o) => o.id))}
                disabled={requesting === 'all'}
                style={{
                  background: 'var(--ink)',
                  color: '#fff',
                  borderRadius: 'var(--radius-pill)',
                  padding: '8px 14px',
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.12em'
                }}
              >
                {requesting === 'all' ? 'REQUESTING…' : `REQUEST ALL RETURNS`}
              </button>
            )}
            <div className="meta">
              {active.length} {active.length === 1 ? 'ITEM' : 'ITEMS'}
            </div>
          </div>
        </div>

        {loading && (
          <div style={{ padding: '20px 22px', color: 'var(--ink-soft)' }}>
            Loading your rotation…
          </div>
        )}

        {error && (
          <div className="empty-grid">
            <div className="h">Couldn&apos;t load your rotation</div>
            <div className="p">{error}</div>
          </div>
        )}

        {!loading && !error && active.length === 0 && (
          <div style={{ padding: '30px 22px', textAlign: 'center' }}>
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                background: 'var(--card)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                color: 'var(--ink-soft)'
              }}
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
                <path d="M21 12a9 9 0 1 1-3-6.7" />
                <path d="M21 4v5h-5" />
              </svg>
            </div>
            <div style={{ fontFamily: 'var(--display)', fontSize: 18, marginBottom: 6 }}>
              NOTHING ON ROTATION YET
            </div>
            <div
              style={{
                fontSize: 13,
                color: 'var(--ink-soft)',
                lineHeight: 1.5,
                marginBottom: 18
              }}
            >
              Browse the rotation and add your first piece to get started.
            </div>
            <Link
              to="/browse"
              className="cta-full"
              style={{ maxWidth: 240, margin: '0 auto', textDecoration: 'none' }}
            >
              <span>BROWSE PIECES</span>
              <Arrow />
            </Link>
          </div>
        )}

        {active.map((o) => {
          // First order_item gives us the headline image + name to show.
          const firstItem = o.order_items?.[0];
          const imgUrl = firstItem?.item?.main_image_url ?? null;
          const itemCount = o.order_items?.length ?? 0;
          const headline =
            firstItem?.item_name_snapshot ??
            `${o.credits_used} ${o.credits_used === 1 ? 'credit' : 'credits'}`;
          const brand = firstItem?.brand_name_snapshot ?? `ORDER #${o.order_number}`;
          const returnRequested = o.status === 'returning';
          return (
            <div
              key={o.id}
              className="active-card"
              onClick={() => setViewingOrder(o)}
              style={{ cursor: 'pointer' }}
            >
              <div
                className="img"
                style={imgUrl ? { backgroundImage: `url(${imgUrl})` } : undefined}
              />
              <div>
                <div className="brand-name">{brand}</div>
                <div className="item-name">
                  {headline}
                  {itemCount > 1 && (
                    <span style={{ color: 'var(--ink-soft)', fontWeight: 400 }}>
                      {' '}+ {itemCount - 1} more
                    </span>
                  )}
                </div>
                <div className="sub">
                  {labelStatus(o.status)} · Placed {formatDate(o.created_at)}
                  {o.delivered_at ? ` · Delivered ${formatDate(o.delivered_at)}` : ''}
                </div>
                {o.return_due_date && (
                  <div className="pill">
                    <CalendarIcon />
                    <div>
                      <span className="l">Return by: {formatDate(o.return_due_date)}</span>
                      <span className="d">{daysFromNow(o.return_due_date)}</span>
                    </div>
                  </div>
                )}
                <div style={{ marginTop: 10 }}>
                  {returnRequested ? (
                    <div
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        background: 'var(--good-bg)',
                        color: 'var(--good)',
                        padding: '8px 12px',
                        borderRadius: 'var(--radius-pill)',
                        fontSize: 11,
                        fontWeight: 600
                      }}
                    >
                      <Check small /> Return label requested
                    </div>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        void requestReturn(o.id);
                      }}
                      disabled={requesting === o.id || requesting === 'all'}
                      style={{
                        background: 'var(--ink)',
                        color: '#fff',
                        borderRadius: 'var(--radius-pill)',
                        padding: '8px 14px',
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: '0.12em',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6
                      }}
                    >
                      {requesting === o.id ? 'REQUESTING…' : 'REQUEST RETURN LABEL'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {active.length > 0 && (
          <>
            <div className="section-head" style={{ paddingTop: 16 }}>
              <div className="ttl">YOUR TIMELINE</div>
              <Link to="/how-it-works" className="link">
                View full <Arrow small />
              </Link>
            </div>
            <div className="timeline">
              <div className="row">
                <TimelineStep day="DAY 1" lbl="Delivered" icon={<PackageIcon />} />
                <TimelineStep day="DAY 25–30" lbl="Label sent" icon={<MailIcon />} />
                <TimelineStep day="DAY 30" lbl="Return due" icon={<CalendarIcon />} warn />
                <TimelineStep day="DAY 32" lbl="Grace ends" icon={<TruckIcon />} />
                <TimelineStep day="DAY 33+" lbl="£10/day" icon={<AlertIcon />} />
              </div>
            </div>
          </>
        )}

        {past.length > 0 && (
          <>
            <div className="section-head" style={{ paddingTop: 16 }}>
              <div className="ttl">PAST ROTATIONS</div>
              <div className="meta">
                {past.length} {past.length === 1 ? 'ORDER' : 'ORDERS'}
              </div>
            </div>
            {past.map((o) => (
              <div
                key={o.id}
                className="past-card"
                onClick={() => setViewingOrder(o)}
                style={{ cursor: 'pointer' }}
              >
                <div className="img" />
                <div>
                  <div className="brand-name">ORDER #{o.order_number}</div>
                  <div className="item-name">
                    {o.credits_used} {o.credits_used === 1 ? 'credit' : 'credits'} ·{' '}
                    {labelStatus(o.status)}
                  </div>
                  <div className="meta">
                    {formatDate(o.created_at)}
                    {o.returned_at ? ` → returned ${formatDate(o.returned_at)}` : ''}
                  </div>
                </div>
                <Chev />
              </div>
            ))}
          </>
        )}

        <div className="help-extend">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4">
            <path d="M20 12V8a2 2 0 00-2-2h-3l-3-3-3 3H6a2 2 0 00-2 2v4M4 12v4a2 2 0 002 2h12a2 2 0 002-2v-4" />
          </svg>
          <div>
            <div className="h">Need to extend?</div>
            <div className="p">Contact us before your return due date.</div>
          </div>
          <Link to="/contact" className="btn">
            CONTACT US
          </Link>
        </div>
      </div>

      {viewingOrder && (
        <OrderItemsModal
          order={viewingOrder}
          onClose={() => setViewingOrder(null)}
        />
      )}
    </section>
  );
}

function OrderItemsModal({
  order,
  onClose
}: {
  order: OrderWithItems;
  onClose: () => void;
}) {
  const items = order.order_items ?? [];
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center'
      }}
      onClick={onClose}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(14,14,14,0.55)'
        }}
      />
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          background: 'var(--bg, #f4efe6)',
          width: '100%',
          maxWidth: 560,
          maxHeight: '85vh',
          overflowY: 'auto',
          borderRadius: '20px 20px 0 0',
          padding: '20px 22px 32px'
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16
          }}
        >
          <div>
            <div
              style={{
                fontSize: 11,
                letterSpacing: '0.14em',
                color: 'var(--ink-soft)',
                fontWeight: 700
              }}
            >
              ORDER #{order.order_number}
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, marginTop: 4 }}>
              {items.length} {items.length === 1 ? 'item' : 'items'} ·{' '}
              {order.credits_used}{' '}
              {order.credits_used === 1 ? 'credit' : 'credits'}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: 24,
              lineHeight: 1,
              color: 'var(--ink, #0e0e0e)',
              padding: 4
            }}
          >
            ×
          </button>
        </div>

        {items.length === 0 ? (
          <div style={{ color: 'var(--ink-soft)', textAlign: 'center', padding: 24 }}>
            No items recorded on this order.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {items.map((oi) => {
              const img = oi.item?.main_image_url ?? null;
              const slug = oi.item?.slug;
              const card = (
                <>
                  <div
                    style={{
                      width: 72,
                      height: 90,
                      borderRadius: 8,
                      background: img
                        ? `url(${img}) center/cover`
                        : 'var(--bg-3, #efe9dc)',
                      flexShrink: 0
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 11,
                        letterSpacing: '0.08em',
                        color: 'var(--ink-soft)'
                      }}
                    >
                      {oi.brand_name_snapshot}
                    </div>
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: 14,
                        marginTop: 2
                      }}
                    >
                      {oi.item_name_snapshot}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: 'var(--ink-soft)',
                        marginTop: 4
                      }}
                    >
                      {oi.credit_price_snapshot}{' '}
                      {oi.credit_price_snapshot === 1 ? 'credit' : 'credits'}
                      {oi.returned_at
                        ? ` · returned ${new Date(
                            oi.returned_at
                          ).toLocaleDateString('en-GB')}`
                        : ''}
                    </div>
                  </div>
                </>
              );
              return slug ? (
                <Link
                  to={`/browse/${slug}`}
                  key={oi.id}
                  onClick={onClose}
                  style={{
                    display: 'flex',
                    gap: 12,
                    padding: 12,
                    background: 'var(--card, #fff)',
                    borderRadius: 12,
                    textDecoration: 'none',
                    color: 'inherit'
                  }}
                >
                  {card}
                </Link>
              ) : (
                <div
                  key={oi.id}
                  style={{
                    display: 'flex',
                    gap: 12,
                    padding: 12,
                    background: 'var(--card, #fff)',
                    borderRadius: 12
                  }}
                >
                  {card}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function TimelineStep({
  day,
  lbl,
  icon,
  warn
}: {
  day: string;
  lbl: string;
  icon: React.ReactNode;
  warn?: boolean;
}) {
  return (
    <div className={`step${warn ? ' warn' : ''}`}>
      <div className="dot">{icon}</div>
      <div className="day">{day}</div>
      <div className="lbl">{lbl}</div>
    </div>
  );
}

function labelStatus(s: string): string {
  return s.replace(/_/g, ' ');
}
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}
function daysFromNow(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  const days = Math.round(ms / (24 * 60 * 60 * 1000));
  if (days < 0) return `${Math.abs(days)} days overdue`;
  if (days === 0) return 'Due today';
  return `${days} ${days === 1 ? 'day' : 'days'} left`;
}

function Check({ small = false }: { small?: boolean }) {
  const s = small ? 12 : 16;
  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function Info() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v4M12 16h0" />
    </svg>
  );
}
function CalendarIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M3 9h18M8 3v4M16 3v4" />
    </svg>
  );
}
function PackageIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M21 16V8l-9-5-9 5v8l9 5 9-5z" />
    </svg>
  );
}
function MailIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 7l9 6 9-6" />
    </svg>
  );
}
function TruckIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="1" y="7" width="15" height="10" rx="1" />
      <path d="M16 11h4l3 3v3h-7" />
      <circle cx="6" cy="19" r="2" />
      <circle cx="19" cy="19" r="2" />
    </svg>
  );
}
function AlertIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M12 2 1 22h22L12 2z" />
      <path d="M12 9v6M12 18h0" />
    </svg>
  );
}
function Arrow({ small = false }: { small?: boolean }) {
  const s = small ? 14 : 20;
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  );
}
function Chev() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
      <polyline points="9 6 15 12 9 18" />
    </svg>
  );
}
